import React, { useCallback, useEffect, useState } from "react";
import { message, Skeleton } from "antd";
import { FiRefreshCw, FiUser } from "react-icons/fi";
import Dashboard from "./Dashboard";

const getApiUrl = (path) => {
  const baseUrl = process.env.REACT_APP_HAPS_MEDIA_BASE_URL || "";
  return `${baseUrl.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;
};

const readApiResponse = async (response) => {
  const body = await response.text();
  if (!body) return {};

  try {
    return JSON.parse(body);
  } catch {
    return { message: body };
  }
};

const getRequestError = (response, result) => {
  if (response.status === 401 || response.status === 403) {
    return "Your login session has expired. Please log out and log in again.";
  }

  if (response.status === 404) {
    return "We could not find a profile for this account.";
  }

  if (result?.error?.validation) {
    return "Please check the highlighted profile fields.";
  }

  const apiMessage = typeof result?.error === "string" ? result.error : result?.message;
  return apiMessage || `The request failed (${response.status}).`;
};

const UpdateProfile = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [form, setForm] = useState({ name: "", email: "", contact: "" });
  const [errors, setErrors] = useState({});

  const getCustomerData = useCallback(async () => {
    setLoading(true);
    setLoadError("");

    try {
      const customerId = localStorage.getItem("CID");
      const token = localStorage.getItem("token");

      if (!customerId || !token) {
        throw new Error("Your login session is missing. Please log out and log in again.");
      }

      const response = await fetch(getApiUrl(`customer/getCustomerById/${customerId}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await readApiResponse(response);
      const customer = Array.isArray(result.data) ? result.data[0] : result.data;

      if (!response.ok || result.status !== 200 || !customer) {
        throw new Error(
          customer ? getRequestError(response, result) :
            (response.ok && result.status === 200
              ? "We could not find a profile for this account."
              : getRequestError(response, result))
        );
      }

      setForm({
        name: customer.name || "",
        email: customer.email || "",
        contact: customer.mobile_no || "",
      });
    } catch (error) {
      setLoadError(error.message || "We could not fetch your profile details. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getCustomerData();
  }, [getCustomerData]);

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: "" }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = "Please enter your full name.";
    if (!form.contact.trim()) nextErrors.contact = "Please enter your mobile number.";
    else if (!/^\d{10}$/.test(form.contact)) nextErrors.contact = "Enter a valid 10-digit mobile number.";
    if (!form.email.trim()) nextErrors.email = "Please enter your email address.";
    else if (!/\S+@\S+\.\S+/.test(form.email)) nextErrors.email = "Enter a valid email address.";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleUpdateProfile = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      const customerId = localStorage.getItem("CID");
      const token = localStorage.getItem("token");

      if (!customerId || !token) {
        throw new Error("Your login session is missing. Please log out and log in again.");
      }

      const response = await fetch(getApiUrl(`customer/updateCustomer/${customerId}`), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          mobile_no: form.contact,
        }),
      });
      const result = await readApiResponse(response);

      if (response.ok && result.status === 200 && !result.error) {
        localStorage.setItem("CN", form.name.trim());
        messageApi.success("Profile updated successfully.");
        return;
      }

      const validation = result?.error?.validation || {};
      setErrors({
        contact: validation.mobile_no || "",
        email: validation.email || "",
      });
      throw new Error(getRequestError(response, result));
    } catch (error) {
      messageApi.error(error.message || "Unable to update your profile.");
    } finally {
      setSaving(false);
    }
  };

  const fieldClass =
    "mt-2 w-full border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-[#858653] focus:ring-1 focus:ring-[#858653] disabled:bg-gray-50";

  return (
    <Dashboard>
      {contextHolder}
      <section className="mx-auto max-w-4xl">
        <div className="border-b border-gray-200 pb-6">
          <p className="text-xs font-bold uppercase text-[#858653]">My account</p>
          <div className="mt-2 flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center bg-[#f5f3f1] text-[#858653]">
              <FiUser size={20} aria-hidden="true" />
            </span>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Profile details</h1>
              <p className="mt-1 text-sm text-gray-500">Keep your contact information current.</p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="min-h-[280px] space-y-5 rounded-xl border p-6" role="status" aria-label="Loading profile">
            <Skeleton active title={{ width: "35%" }} paragraph={{ rows: 2 }} />
            <Skeleton active title={{ width: "25%" }} paragraph={{ rows: 4 }} />
          </div>
        ) : loadError ? (
          <div className="flex min-h-[280px] flex-col items-center justify-center text-center">
            <p className="max-w-md text-sm text-gray-600">{loadError}</p>
            <button
              type="button"
              onClick={getCustomerData}
              className="mt-5 inline-flex items-center gap-2 border border-gray-900 px-5 py-3 text-sm font-semibold hover:bg-gray-900 hover:text-white"
            >
              <FiRefreshCw aria-hidden="true" /> Retry
            </button>
          </div>
        ) : (
          <form onSubmit={handleUpdateProfile} className="py-8">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label htmlFor="profile-name" className="text-sm font-medium text-gray-700">Full name</label>
                <input id="profile-name" name="name" value={form.name} onChange={updateField} className={fieldClass} autoComplete="name" />
                {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
              </div>
              <div>
                <label htmlFor="profile-contact" className="text-sm font-medium text-gray-700">Phone number</label>
                <input id="profile-contact" name="contact" type="tel" inputMode="numeric" maxLength={10} value={form.contact} onChange={updateField} className={fieldClass} autoComplete="tel" />
                {errors.contact && <p className="mt-1 text-xs text-red-600">{errors.contact}</p>}
              </div>
              <div className="md:col-span-2">
                <label htmlFor="profile-email" className="text-sm font-medium text-gray-700">Email address</label>
                <input id="profile-email" name="email" type="email" value={form.email} onChange={updateField} className={fieldClass} autoComplete="email" />
                {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
              </div>
            </div>

            <div className="mt-8 flex justify-end border-t border-gray-200 pt-6">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex min-h-[46px] w-full items-center justify-center bg-[#20211d] px-7 py-3 text-sm font-semibold text-white hover:bg-black disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {saving ? "Saving changes..." : "Save changes"}
              </button>
            </div>
          </form>
        )}
      </section>
    </Dashboard>
  );
};

export default UpdateProfile;
